import { HeadlineH1, HeadlineH4Regular } from '@nect/ui/component/headline';
import { IconHomeCaseFill } from '@nect/ui/component/icon/auto/home-case-fill';
// import { IconLogoNect } from '@nect/ui/component/icon/auto/logo-nect';
import { Tag } from '@nect/ui/component/tag';
import {
  TextBodyLargeParagraph,
  TextBodySmallParagraph
} from '@nect/ui/component/text';
import { Color } from '@nect/ui/theme/color';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { FaqStyled } from '../components/faq/Faq.styled';
import { Footer } from '../components/Footer/Footer';
import { Header } from '../components/Header/Header';
import { routes } from '../constants/constants';
import { extractUrlParam } from '../helper/extractUrlParam';
import { setPublicSPID } from '../helper/publicSPID';
import { useLocation } from '../react-router-hooks';

const BoxContainerStyled = styled.div`
  display: flex;
  justify-content: space-between;
  @media (max-width: 768px) {
    flex-direction: column;
  }

  margin-top: 30px;
`;

const BoxNectStyled = styled(FaqStyled.Box)`
  max-width: 458px;
  border: 1px solid ${({ theme }) => theme.color[Color.nectGreen500]};
  border-radius: ${({ theme }) => theme.borderRadius.M};
  background: ${({ theme }) => theme.color[Color.nectGreenOpacity5]};
  cursor: pointer;

  height: 100%;
  margin-top: 0;

  h4 {
    margin-top: ${({ theme }) => theme.spacing.XXS};
    margin-bottom: ${({ theme }) => theme.spacing.XXXS};
  }

  &:hover {
    background: ${({ theme }) => theme.color[Color.nectGreen500]};
    * {
      color: white !important;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    margin-left: 0 !important;
  }
`;

const BoxPartnerStyled = styled(BoxNectStyled)`
  border: 1px solid ${({ theme }) => theme.color[Color.primaryPurple500]};
  background: ${({ theme }) => theme.color[Color.primaryPurpleOpacity5]};

  margin-left: 16px;

  &:hover {
    background: ${({ theme }) => theme.color[Color.primaryPurple500]};
  }

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 0;
  }
`;

const BoxTopQuestionStyled = styled.div`
  background: ${({ theme }) => theme.color[Color.snackInfoPad]};
  padding: ${({ theme }) => `${theme.spacing.L} ${theme.spacing.M}`};
  margin-top: ${({ theme }) => theme.spacing.L};
  border-radius: ${({ theme }) => theme.borderRadius.M};

  .small {
    display: none;
  }

  img {
    max-width: 100%;
    width: 100%;
  }
  @media (max-width: 768px) {
    > div:first-child {
      margin-bottom: 16px !important;
    }

    margin-bottom: 32px;

    .wide {
      display: none;
    }
    .small {
      display: block;
    }
  }
`;

const BoxTopQuestionTagsStyled = styled.div`
  --gap: 24px;
  display: inline-flex;
  flex-wrap: wrap;
  margin: calc(-1 * var(--gap)) 0 0 calc(-1 * var(--gap));
  width: calc(100% + var(--gap));

  > * {
    margin: 16px 0 0 19px;
  }
`;

const FaqStartContainerStyled = styled(FaqStyled.Container)`
  max-width: 940px;
`;

const Title = styled(HeadlineH1)`
  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 31px;
    text-align: left;
  }
`;

const SubTitle = styled(TextBodyLargeParagraph)`
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 19px;
    text-align: left;
  }
`;

const FaqStartContainerMainStyled = styled(FaqStyled.ContainerMain)`
  margin-top: 60px;
  @media (max-width: 768px) {
    margin-top: 0;
  }
`;

export const FaqStartScreen = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { location, navigate } = useLocation();
  const [paramsState, setParamsState] = useState<any>();

  useEffect(() => {
    const element = document.getElementById(id);

    if (!element) {
      return;
    }

    const top = element.offsetTop - 100;
    window.scroll({ top, behavior: 'auto' });
  }, [id]);

  useEffect(() => {
    if (!location.state || !location.state.uuid) {
      const url = location.search;
      const paramsState = {
        uuid: extractUrlParam({ url, type: 'uuid' }),
        model: extractUrlParam({ url, type: 'model' }),
        appVersion: extractUrlParam({ url, type: 'appVersion' }),
        systemVersion: extractUrlParam({ url, type: 'systemVersion' }),
        customer: extractUrlParam({ url, type: 'customer' })
      };
      setParamsState(paramsState);
      setPublicSPID({ uuid: paramsState.uuid });
    } else {
      setParamsState(location.state);
    }
  }, [location]);

  const topQuestions = [
    {
      color: Color.nectGreen500,
      text: t('Where can I download the Nect app?'),
      url: routes.FAQCONTENT.replace(':idCategory', 'downloadapp').replace(
        ':idSubCategory',
        'general'
      )
    },
    {
      color: Color.primaryBlue500,
      text: t('Where can I find identification instructions?'),
      url: routes.FAQSUBCATEGORY.replace(':idCategory', 'flow')
    },
    {
      color: Color.grey400,
      text: t('Where can I find the Case ID?'),
      url: routes.FAQCONTENT.replace(':idCategory', 'ididentifier').replace(
        ':idSubCategory',
        'general'
      )
    },
    {
      color: Color.primaryPurple500,
      text: t('How secure is my data with Nect?'),
      url: routes.FAQCONTENT.replace(':idCategory', 'gdpr').replace(
        ':idSubCategory',
        'general'
      )
    },
    {
      color: Color.primaryPurple500,
      text: t('How does the Case ID protect my data?'),
      url: routes.FAQSUBCATEGORY.replace(':idCategory', 'caseid')
    },
    // {
    //   color: Color.primaryOrange500,
    //   text: t('What to do if the QR Code does not work?'),
    //   url: ''
    // },
    {
      color: Color.primaryOrange500,
      text: t('What is the Jump Page and how do I use it?'),
      url: routes.FAQCONTENT.replace(':idCategory', 'jumppage').replace(
        ':idSubCategory',
        'general'
      )
    }
  ];

  return (
    <div className="root">
      <Header />
      <div id="main" className="main">
        <FaqStartContainerMainStyled id="main">
          <FaqStartContainerStyled>
            <Title align="center">{t('We are here to help')}</Title>
            <SubTitle
              style={{ marginTop: 8 }}
              color={Color.grey600}
              align="center"
            >
              {t(
                'Our team of experts has compiled helpful tips and answers for the most frequently asked questions, so your identification will go smoothly.'
              )}
            </SubTitle>

            <BoxContainerStyled>
              <a href={routes.FAQMAIN}>
                <BoxNectStyled>
                  <svg
                    viewBox="0 0 111 98"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon"
                    style={{
                      fontSize: 24,
                      width: 24,
                      height: 24,
                      color: 'rgb(3, 175, 167)'
                    }}
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M93.255 27.106c-1.042-1.489-2.679-2.532-4.464-2.532H73.91c-2.826 0-5.356 1.49-6.695 3.873L55.313 49l22.765 39.468c1.19 2.085 1.19 4.468 0 6.404S74.805 98 72.573 98H31.507c-2.828 0-5.357-1.49-6.696-3.873L1.004 52.872a7.967 7.967 0 010-7.745l9.374-16.68c1.34-2.383 4.018-3.873 6.696-3.873h15.028c1.934 0 3.57.894 4.463 2.532.893 1.49.893 3.426 0 5.064L26.894 49l-9.671 16.83c-.893 1.489-.893 3.425 0 5.064 1.041 1.489 2.678 2.531 4.463 2.531h15.028c2.827 0 5.357-1.489 6.696-3.872L55.313 49 32.697 9.532c-1.19-2.085-1.19-4.468 0-6.404C33.887 1.19 35.97 0 38.202 0h41.066c2.827 0 5.357 1.49 6.696 3.872l23.806 41.256a7.963 7.963 0 010 7.744l-9.671 16.681c-1.34 2.383-4.017 3.873-6.696 3.873H78.375c-1.934 0-3.57-.894-4.463-2.532-.893-1.49-.893-3.426 0-5.064L83.583 49l9.672-16.83c.892-1.49.892-3.425 0-5.064z"
                      fill="currentColor"
                    ></path>
                  </svg>

                  <HeadlineH4Regular>
                    {t('Questions about our product')}
                  </HeadlineH4Regular>

                  <TextBodySmallParagraph color={Color.grey600}>
                    {t(
                      'Here you will find answers to frequently asked questions and information on other topics such as our app, the identification process and data protection.'
                    )}
                  </TextBodySmallParagraph>
                </BoxNectStyled>
              </a>

              <a href={routes.FAQSELECTION}>
                <BoxPartnerStyled>
                  <IconHomeCaseFill color={Color.primaryPurple500} size={24} />

                  <HeadlineH4Regular>
                    {t('Questions about our partner companies')}
                  </HeadlineH4Regular>

                  <TextBodySmallParagraph color={Color.grey600}>
                    {t(
                      'Do you have a question about our partner companies? Here you will find answers and solutions to help you complete your identification process.'
                    )}
                  </TextBodySmallParagraph>
                </BoxPartnerStyled>
              </a>
            </BoxContainerStyled>

            <BoxTopQuestionStyled>
              <div style={{ position: 'relative', marginBottom: 13 }}>
                <img
                  src={require('../assets/top-question-banner-wide.svg')}
                  className="wide"
                />
                <img
                  src={require('../assets/top-question-banner-small.svg')}
                  className="small"
                />
                <HeadlineH4Regular
                  style={{ position: 'absolute', top: 16, left: 24 }}
                >
                  {t('Top Questions')}
                </HeadlineH4Regular>
              </div>

              <BoxTopQuestionTagsStyled>
                {topQuestions.map(item => (
                  <Tag
                    key={item.text}
                    content={item.text}
                    dotColor={item.color}
                    onClick={() => navigate(item.url)}
                    // style={{ marginRight: 25, marginBottom: 16 }}
                  />
                ))}
              </BoxTopQuestionTagsStyled>
            </BoxTopQuestionStyled>
          </FaqStartContainerStyled>
        </FaqStartContainerMainStyled>
      </div>
      <Footer />
    </div>
  );
};
